<template>
  <div class="search-page">
    <div class="search-card">
      <div class="search-header">
        <h2>活動查詢</h2>
      </div>

      <form @submit.prevent="searchActivities" class="search-form">
        <div class="search-row">
          <div class="search-field">
            <label>關鍵字</label>
            <input v-model="searchTerm" type="text" placeholder="請輸入活動名稱、地點或講者" />
          </div>
          <div class="search-field">
            <label>類別</label>
            <select v-model="category">
              <option value="">全部</option>
              <option v-for="opt in categoryOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="search-field">
            <label>開始日期（從）</label>
            <input v-model="startDateFrom" type="date" />
          </div>
          <div class="search-buttons">
            <button type="submit" class="btn-search">搜尋</button>
            <button type="button" @click="clearFilters" class="btn-clear">清除條件</button>
          </div>
        </div>
      </form>
    </div>

    <!-- 搜尋結果統計 -->
    <div v-if="hasSearched" class="search-stats">
      共找到 <strong>{{ activities.length }}</strong> 筆活動
    </div>

    <div v-if="activities.length === 0 && hasSearched" class="empty-result">
      <p>尚無符合條件的活動</p>
    </div>

    <ul v-else-if="activities.length > 0" class="search-results">
      <li v-for="act in activities" :key="act.id" class="search-item">
        <div class="item-header">
          <h3 @click="goToDetail(act)">{{ act.title }}</h3>
          <span class="category-tag" v-if="act.category">{{ formatCategory(act.category) }}</span>
        </div>
        <p class="item-desc">{{ stripHtml(act.description) }}</p>
        <div class="item-meta">
          <span class="meta-item">日期：{{ formatDate(act.start_date || act.activity_date) }} ~ {{ formatDate(act.end_date) }}</span>
          <span class="meta-item" v-if="act.location">地點：{{ act.location }}</span>
          <span class="meta-item">
            自主學習點數：{{ act.learning_hours || 0 }}
            <span v-if="act.needs_reflection && act.reflection_points > 0" class="reflection-bonus">（繳交心得可再獲得 {{ act.reflection_points }} 點）</span>
          </span>
          <span class="meta-item" v-if="act.speaker">講者：{{ act.speaker }}</span>
        </div>

        <div class="action-buttons">
          <button @click="goToDetail(act)" class="btn-detail">查看詳情</button>

          <button
            v-if="isLoggedIn"
            @click.prevent="toggleRegistration(act)"
            :class="['btn-register', isRegistered(act.id) ? 'registered' : 'not-registered', btnUnavailable(act) ? 'unavailable' : '']"
            :disabled="isProcessing[act.id] || btnUnavailable(act)"
            type="button"
          >
            {{ regBtnLabel(act) }}
          </button>

          <span v-else class="login-hint">
            <router-link to="/login">登入</router-link>後可報名
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { getActivities, registerActivity, cancelRegistration, getUserActivities } from '@/api.js'
import { categoryLabel, CATEGORY_FILTER_OPTIONS } from '@/utils/category.js'

export default {
  name: 'Search',
  data() {
    return {
      searchTerm: '',
      category: '',
      categoryOptions: CATEGORY_FILTER_OPTIONS,
      startDateFrom: '',
      activities: [],
      hasSearched: false,
      userRegistrations: [],
      isProcessing: {},
      isLoggedIn: false
    }
  },
  created() {
    this.checkLoginStatus()
    this.searchActivities()
    if (this.isLoggedIn) {
      this.loadUserRegistrations()
    }
  },
  watch: {
    '$route'() {
      this.checkLoginStatus()
      if (this.isLoggedIn) {
        this.loadUserRegistrations()
      }
    }
  },
  methods: {
    checkLoginStatus() {
      const token = localStorage.getItem('access_token')
      this.isLoggedIn = !!token
    },

    // 去除活動簡述的 HTML 標籤，搜尋結果只顯示純文字摘要
    stripHtml(html) {
      if (!html) return ''
      try {
        const doc = new DOMParser().parseFromString(String(html), 'text/html')
        return (doc.body.textContent || '').trim()
      } catch (e) {
        return String(html).replace(/<[^>]*>/g, '')
      }
    },

    async loadUserRegistrations() {
      if (!this.isLoggedIn) {
        this.userRegistrations = []
        return
      }
      try {
        const response = await getUserActivities()
        this.userRegistrations = response.data.map(act => act.id)
      } catch (error) {
        console.error('載入報名記錄失敗:', error)
        this.userRegistrations = []
        if (error.response?.status === 401) {
          this.isLoggedIn = false
        }
      }
    },

    isRegistered(activityId) {
      return this.userRegistrations.includes(activityId)
    },

    async toggleRegistration(activity) {
      if (!this.isLoggedIn) {
        this.$router.push('/login')
        return
      }

      this.isProcessing[activity.id] = true

      try {
        if (this.isRegistered(activity.id)) {
          if (confirm(`確定要取消報名「${activity.title}」嗎？`)) {
            await cancelRegistration(activity.id)
            this.userRegistrations = this.userRegistrations.filter(id => id !== activity.id)
            alert('取消報名成功')
          }
        } else {
          await registerActivity(activity.id)
          this.userRegistrations.push(activity.id)
          alert(`成功報名「${activity.title}」`)
        }
      } catch (error) {
        console.error('操作失敗:', error)
        const errorMsg = error.response?.data?.detail || '操作失敗，請稍後再試'
        alert(errorMsg)
        if (error.response?.status === 401) {
          this.isLoggedIn = false
          localStorage.removeItem('access_token')
          localStorage.removeItem('token_type')
          localStorage.removeItem('user_info')
        }
      } finally {
        this.isProcessing[activity.id] = false
      }
    },

    async searchActivities() {
      try {
        this.hasSearched = true
        const response = await getActivities({ skip: 0, limit: 1000, show_all: true })
        let data = response.data

        if (this.searchTerm) {
          const kw = this.searchTerm.toLowerCase()
          data = data.filter((a) =>
            a.title.toLowerCase().includes(kw) ||
            (a.description && this.stripHtml(a.description).toLowerCase().includes(kw)) ||
            (a.speaker && a.speaker.toLowerCase().includes(kw)) ||
            (a.location && a.location.toLowerCase().includes(kw))
          )
        }

        if (this.category) {
          // 現行僅兩類，篩選以顯示標籤（惠蓀講座 / 其他活動）比對，涵蓋所有舊代碼
          data = data.filter((a) => categoryLabel(a.category, '') === this.category)
        }

        if (this.startDateFrom) {
          data = data.filter((a) => {
            if (!a.start_date && !a.activity_date) return false
            const activityDate = new Date(a.start_date || a.activity_date)
            const fromDate = new Date(this.startDateFrom)
            fromDate.setHours(0, 0, 0, 0)
            return activityDate >= fromDate
          })
        }

        data.sort((a, b) => {
          const dateA = a.start_date || a.activity_date
          const dateB = b.start_date || b.activity_date
          if (!dateA && !dateB) return 0
          if (!dateA) return 1
          if (!dateB) return -1
          return new Date(dateB) - new Date(dateA)
        })

        this.activities = data
      } catch (error) {
        console.error('搜尋失敗：', error)
        this.activities = []
        alert('搜尋活動時發生錯誤，請稍後再試')
      }
    },

    clearFilters() {
      this.searchTerm = ''
      this.category = ''
      this.startDateFrom = ''
      this.searchActivities()
    },

    formatDate(dateString) {
      if (!dateString) return '待定'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return '日期格式錯誤'
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}`
      } catch (error) {
        return '日期解析失敗'
      }
    },

    formatCategory(cat) {
      return categoryLabel(cat, '')
    },

    goToDetail(act) {
      this.$router.push(`/activity/${act.id}`)
    },

    // 是否額滿（有設上限且已達）
    isFull(act) {
      const max = parseInt(act.max_participants, 10)
      if (!max || max <= 0) return false
      return (act.registration_count || 0) >= max
    },
    // 是否已過報名截止（reg_deadline；未設則以活動開始時間為截止）
    isClosed(act) {
      const d = act.reg_deadline || act.start_date || act.activity_date
      if (!d) return false
      const dt = new Date(d)
      return !isNaN(dt.getTime()) && new Date() > dt
    },
    // 活動是否已結束（以結束時間為準，無結束則用開始時間）
    isEnded(act) {
      const d = act.end_date || act.start_date || act.activity_date
      if (!d) return false
      const dt = new Date(d)
      return !isNaN(dt.getTime()) && new Date() > dt
    },
    // 按鈕是否應禁用：未報名且額滿/截止；或已報名但活動已結束(不可取消)
    btnUnavailable(act) {
      if (this.isRegistered(act.id)) return this.isEnded(act)
      return this.isFull(act) || this.isClosed(act)
    },
    // 報名按鈕文字：已報名→取消報名(結束則不可取消)；未報名→額滿/截止顯示原因、不可點
    regBtnLabel(act) {
      if (this.isProcessing[act.id]) return '處理中...'
      if (this.isRegistered(act.id)) return this.isEnded(act) ? '活動已結束，無法取消報名' : '取消報名'
      if (this.isFull(act)) return '已額滿'
      if (this.isClosed(act)) return '報名已截止'
      return '報名'
    }
  }
}
</script>

<style scoped>
.search-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
}

.search-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-bottom: 24px;
}

.search-header {
  background: linear-gradient(135deg, #1a5c2e 0%, #2d8a4e 100%);
  padding: 18px 28px;
}

.search-header h2 {
  color: #fff;
  margin: 0;
  font-size: 22px;
  font-weight: 700;
}

.search-form {
  padding: 24px 28px;
}

.search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.search-field {
  flex: 1;
  min-width: 180px;
}

.search-field label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #1a5c2e;
}

.search-field input,
.search-field select {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.search-field input:focus,
.search-field select:focus {
  outline: none;
  border-color: #1a5c2e;
  box-shadow: 0 0 0 3px rgba(26, 92, 46, 0.1);
}

.search-buttons {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.btn-search {
  padding: 10px 24px;
  background: linear-gradient(135deg, #1a5c2e 0%, #2d8a4e 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(26, 92, 46, 0.3);
}

.btn-search:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(26, 92, 46, 0.4);
}

.btn-clear {
  padding: 10px 24px;
  background: #f7fafc;
  color: #4a5568;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
}

.btn-clear:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
}

.search-stats {
  margin-bottom: 16px;
  color: #555;
  font-size: 14px;
  padding: 0 4px;
}

.empty-result {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
}

.search-results {
  list-style: none;
  padding: 0;
  margin: 0;
}

.search-item {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border-left: 4px solid #1a5c2e;
  transition: all 0.3s;
}

.search-item:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.item-header h3 {
  color: #1a5c2e;
  margin: 0;
  font-size: 18px;
  cursor: pointer;
  transition: color 0.2s;
}

.item-header h3:hover {
  color: #2d8a4e;
  text-decoration: underline;
}

.category-tag {
  display: inline-block;
  padding: 3px 10px;
  background: #f0f7f2;
  color: #1a5c2e;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.item-desc {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.meta-item {
  font-size: 13px;
  color: #888;
}

.meta-item .reflection-bonus {
  color: #2e7d32;
}

/* 按鈕組 */
.action-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn-detail {
  padding: 8px 18px;
  background: #fff;
  color: #1a5c2e;
  border: 2px solid #1a5c2e;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
}

.btn-detail:hover {
  background: #1a5c2e;
  color: #fff;
}

.btn-register {
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
}

.btn-register.not-registered {
  background: linear-gradient(135deg, #1a5c2e 0%, #2d8a4e 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(26, 92, 46, 0.3);
}

.btn-register.not-registered:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(26, 92, 46, 0.4);
}

.btn-register.registered {
  background: #dc3545;
  color: white;
}

.btn-register.registered:hover:not(:disabled) {
  background: #c82333;
}

.btn-register:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-register.unavailable {
  background: #9e9e9e;
  color: #fff;
  opacity: 1;
  cursor: not-allowed;
}

.login-hint {
  color: #888;
  font-size: 14px;
}

.login-hint a {
  color: #1a5c2e;
  text-decoration: none;
  font-weight: 600;
}

.login-hint a:hover {
  text-decoration: underline;
}

/* 響應式 */
@media (max-width: 768px) {
  .search-page {
    padding: 12px;
  }

  .search-row {
    flex-direction: column;
  }

  .search-field {
    min-width: auto;
  }

  .search-buttons {
    justify-content: center;
    width: 100%;
  }

  .search-buttons button {
    flex: 1;
  }

  .search-form {
    padding: 16px;
  }

  .search-item {
    padding: 16px;
  }

  .item-meta {
    flex-direction: column;
    gap: 4px;
  }

  .action-buttons {
    flex-direction: column;
    width: 100%;
  }

  .btn-detail,
  .btn-register {
    width: 100%;
    text-align: center;
  }
}
</style>
